import { PageController } from '@client/components/_core/layout/PageController';
import { BasicTabs } from '@client/components/_core/navigation/Tabs';
import { EntityDetailsHeader } from '@client/components/_ui/data-display/EntityDetailsHeader';
import { History } from '@client/components/_ui/display/History';
import { BackLink } from '@client/components/_ui/navigation/BackLink';
import { RiskAppetiteLabel } from '@client/components/risks/RiskAppetiteLabel';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { trpcOptions } from '@client/trpc/client';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Skeleton } from 'antd';
import { RiskAppetiteStatementManageDetails } from './RiskAppetiteStatementManageDetails';

export const Route = createFileRoute('/console/$orgId/risk/ras/details/$id')({
    staticData: {
        breadcrumb: {
            title: 'Risk appetite statement details',
        },
        ui: {
            widthVariant: 'default',
        },
    },
    params: {
        parse({ id }) {
            return {
                id: BigInt(id),
            };
        },
    },
    component: RiskAppetiteStatementDetails,
});

function RiskAppetiteStatementDetails() {
    const orgId = useOrgId();
    const { id } = Route.useParams();
    // The same query the details tab reads, so the header shares its cache entry. The tab reports a
    // failure (a missing record sends the user back to the list), so the header stays silent.
    const { data: statement, isPending } = useQuery(
        trpcOptions.risk.appetiteStatement.getRiskAppetiteStatement.queryOptions(
            { id },
            { meta: { errorSurface: 'silent' } },
        ),
    );
    const categoryHierarchy = statement?.categoryHierarchy ?? [];

    return (
        <PageController route={Route}>
            <div className="flex flex-col gap-4">
                <BackLink to="/console/$orgId/risk/ras" params={{ orgId }}>
                    Back to risk appetite statements
                </BackLink>
                {/* Drawn with placeholders while the statement loads, so the tabs do not jump down when it
                    lands. Nothing on a failure: the details tab reports it and leaves the page. */}
                {(statement || isPending) && (
                    <EntityDetailsHeader
                        className="mb-0"
                        title={
                            statement ? (
                                (categoryHierarchy.at(-1) ?? 'Risk appetite statement')
                            ) : (
                                <Skeleton.Input active size="small" className="align-middle" />
                            )
                        }
                        titleAddon={
                            statement && (
                                <RiskAppetiteLabel
                                    size="small"
                                    description={statement.RiskAppetite?.description || ''}
                                    color={statement.RiskAppetite?.color}
                                    name={statement.RiskAppetite?.name}
                                    isDeleted={statement.isDeleted}
                                />
                            )
                        }
                        mainInfo={[
                            {
                                title: 'Risk category',
                                children: statement ? (
                                    categoryHierarchy.join(' > ') || '-'
                                ) : (
                                    <Skeleton.Input
                                        active
                                        size="small"
                                        className="align-top"
                                        classNames={{ content: '!h-5' }}
                                    />
                                ),
                            },
                        ]}
                    />
                )}
                <BasicTabs
                    type="line"
                    items={[
                        {
                            key: 'risk_appetite_statement_details',
                            label: 'Details',
                            destroyOnHidden: true,
                            children: <RiskAppetiteStatementManageDetails />,
                        },
                        {
                            key: 'risk_appetite_statement_history',
                            label: 'History',
                            destroyOnHidden: true,
                            children: <History idValue={id} idPropertyName="riskAppetiteStatementId" />,
                        },
                    ]}
                />
            </div>
        </PageController>
    );
}
